/**
 * Sanitizes text for metadata by removing HTML tags and normalizing content
 * @param {string} text - The text to sanitize
 * @param {number} maxLength - Maximum length for the description
 * @returns {string} Sanitized text suitable for metadata
 */
const sanitizeMetaText = (text, maxLength = 160) => {
  if (!text) return "";

  // Parse into an inert document to strip tags. (Setting innerHTML on an
  // element of the live document, even a detached one, still loads images and
  // fires their onerror handlers, so a pack name like <img onerror=...> would
  // run script for every visitor.)
  const parsed = new DOMParser().parseFromString(String(text), "text/html");

  // Get text content (this removes all HTML tags)
  let cleanText = parsed.body ? parsed.body.textContent || "" : "";

  // Normalize whitespace and special characters
  cleanText = cleanText
    .replace(/\s+/g, " ") // Replace multiple spaces/newlines with single space
    .replace(/^\s+|\s+$/g, "") // Trim start and end
    .replace(/[""]/g, '"') // Normalize quotes
    .replace(/['']/g, "'") // Normalize apostrophes
    .replace(/[<>]/g, "") // Remove any remaining angle brackets
    .replace(/&/g, "and"); // Replace ampersands with 'and'

  // Truncate if needed
  if (cleanText.length > maxLength) {
    cleanText = cleanText.substring(0, maxLength - 3) + "...";
  }

  return cleanText;
};

/**
 * Generates clean metadata for the pack
 * @param {Object} params Pack parameters
 * @returns {Object} Sanitized metadata
 */
export const generatePackMetadata = ({
  packName,
  packDescription,
  weightInGrams,
  id,
  getFormattedWeightInUnit,
}) => {
  const sanitizedName = sanitizeMetaText(packName, 100);
  const sanitizedDescription = sanitizeMetaText(packDescription, 160);

  const weightFormatted = `${getFormattedWeightInUnit(
    weightInGrams,
    false
  )} / ${getFormattedWeightInUnit(weightInGrams, true)}`;

  // Generate clean keywords from pack name
  const keywords = sanitizedName
    ? sanitizedName
        .split(" ")
        .filter((word) => word.length > 1) // Filter out single characters
        .join(",")
    : "";

  const richDescription = `${sanitizedName}: a ${weightFormatted} backpacking gear list on PackWizard. See the full item breakdown, weights and where to buy each piece.${
    sanitizedDescription ? ` ${sanitizedDescription}` : ""
  }`;

  return {
    title: `${sanitizedName} – ${weightFormatted} Gear List | PackWizard`,
    ogTitle: `${sanitizedName} - ${weightFormatted}`,
    ogDescription: sanitizedDescription || richDescription,
    canonicalUrl: `https://www.packwizard.com/s/${id}`,
    ogImage: `/api/image/packPreview?shareableId=${id}`,
    twitterTitle: `${sanitizedName} - ${weightFormatted}`,
    twitterDescription: sanitizedDescription || richDescription,
    twitterImage: `/api/image/packPreview?shareableId=${id}`,
    keywords: keywords,
    description: sanitizeMetaText(richDescription, 160),
  };
};
